import type {
  AppetiteCounts,
  BrokerQuote,
  CarrierIndication,
  MissingField,
  SubmissionCarrier,
} from "@/lib/submissions/brokerSubmissions";
import type { ActionStanding } from "@/lib/submissions/actionItems";
import type { BrokerCarrierSummary } from "@/lib/brokerCarriers";

// The comparison's columns (BRK-S049 → BRK-S053 → BRK-S298), one per carrier,
// from its persisted quotes and this visit's fan-out. A quote PMS marks
// outdated (PMS-S707: the application changed after it was priced) never
// stands in for a carrier while anything current exists (BRK-S341).

/** One comparison column: a carrier's offer (quote) OR its non-quoted fan-out
 *  outcome (declined / referred / errored, with reasons). `carrierId` (from
 *  the fan-out results, or the saved quote's own since PMS-S553) addresses
 *  the carrier for the quote step; without it the action is unavailable. */
export interface OfferColumn {
  key: string;
  carrier: string | null;
  carrierId: string | null;
  carrierProfile?: BrokerCarrierSummary | null;
  quote: BrokerQuote | null;
  outcome: Exclude<CarrierIndication["outcome"], "quoted"> | null;
  reasons: string[];
  /** An appetite-only leg's counts (PMS-S681). */
  appetite: AppetiteCounts | null;
  /** What a needs-information leg still needs (PMS-S706). */
  missingFields: MissingField[];
  /** A carrier Signal quotes by hand, with no quote yet (PMS-S819,
   *  BRK-S395), and the broker's request for it. */
  manual?: { request: ActionStanding | null };
}

// Which columns can be ticked for a quote (BRK-S298): a current priced
// indication, a referral, or an appetite-only leg — each with the fan-out's
// carrier address. A declined or errored market can't be, nor a carrier that
// already quoted, nor an outdated one (it has to be priced again first), nor a column
// with no address (a persisted quote with no fan-out this session). A carrier
// that prices only when it quotes (PMS-S750) is asked again only once its
// price is outdated: asking it otherwise just re-prices the same offer, so
// its next step is its underwriter's (BRK-S364).
export function selectable(col: OfferColumn): boolean {
  if (!col.carrierId) return false;
  if (col.quote?.pricesOnlyAtQuote) return Boolean(col.quote.outdated);
  if (col.quote) return col.quote.kind === "indication" && !col.quote.outdated;
  return col.outcome === "referred" || col.outcome === "appetite_only";
}

/** Whether `a` should speak for its carrier rather than `b`: a current quote
 *  before an outdated one, then a quote before an indication (so a
 *  firmed-up carrier flips in place), then the newest. */
function outranks(a: BrokerQuote, b: BrokerQuote): boolean {
  if (Boolean(a.outdated) !== Boolean(b.outdated)) return !a.outdated;
  if (a.kind !== b.kind) return a.kind === "quote";
  return a.createdAt > b.createdAt;
}

function quoteColumn(
  key: string,
  quote: BrokerQuote,
  carrierId: string | null,
): OfferColumn {
  return {
    key,
    carrier: quote.carrier,
    carrierProfile: quote.carrierProfile,
    carrierId,
    quote,
    outcome: null,
    reasons: [],
    appetite: null,
    missingFields: [],
  };
}

/** A fan-out leg's column: its quote when quoted (null if PMS sent none),
 *  else its outcome. A tolerant reader: an older PMS may send no `reasons` or
 *  `appetite`. */
function legColumn(r: CarrierIndication): OfferColumn | null {
  if (r.outcome === "quoted") {
    return r.quote && r.quote.stage !== "superseded"
      ? quoteColumn(r.carrierId, r.quote, r.carrierId)
      : null;
  }
  return {
    key: r.carrierId,
    carrier: r.carrier,
    carrierProfile: r.carrierProfile,
    carrierId: r.carrierId,
    quote: null,
    outcome: r.outcome,
    reasons: r.reasons ?? [],
    appetite: r.appetite ?? null,
    missingFields: r.missingFields ?? [],
  };
}

/**
 * One column per carrier. A current persisted quote speaks for its carrier;
 * a carrier whose quotes are all outdated is spoken for by this visit's
 * fan-out leg when there is one, and otherwise by its outdated quote, marked
 * as such. The fan-out's other carriers follow. A quoted leg whose persisted
 * quote hasn't landed yet shows its inline quote, so the table never flashes
 * empty between pull and refresh.
 *
 * PMS lists one offer per carrier, its newest quote, else its newest
 * indication (PMS-S707), so an outdated quote stays listed after the
 * markets price that carrier again: the leg is what replaces it. The ranking
 * still takes a list with several, as the dev stub's is.
 *
 * A carrier that prices only when it quotes (PMS-S750) is never priced by the
 * markets' check, whose leg holds only its appetite: its outdated price keeps
 * its column, with the leg's address, to be priced again (BRK-S364).
 */
export function toColumns(
  quotes: BrokerQuote[],
  outcomes: CarrierIndication[] | null,
  manualCarriers: readonly SubmissionCarrier[] = [],
): OfferColumn[] {
  const best = new Map<string, BrokerQuote>();
  for (const q of quotes) {
    if (q.stage === "superseded") continue;
    const key = q.carrierId ?? q.carrier ?? q.id;
    const held = best.get(key);
    if (!held || outranks(q, held)) best.set(key, q);
  }
  const legs = new Map((outcomes ?? []).map((r) => [r.carrierId, r]));
  const placed = new Set<string>();
  const columns: OfferColumn[] = [];
  for (const [key, q] of best) {
    // Only an older quote without an id needs the name join. An ambiguous
    // display name cannot safely address a carrier's questions or quote.
    const legacyMatches = q.carrierId
      ? []
      : (outcomes ?? []).filter((r) => r.carrier === q.carrier);
    const leg = q.carrierId
      ? legs.get(q.carrierId)
      : legacyMatches.length === 1
        ? legacyMatches[0]
        : undefined;
    if (leg) placed.add(leg.carrierId);
    const fresh =
      leg &&
      (leg.outcome === "declined" || (q.outdated && !q.pricesOnlyAtQuote))
        ? legColumn(leg)
        : null;
    // A saved quote names its carrier (PMS-S553): no markets' check is
    // needed to address it (BRK-S346).
    columns.push(
      fresh ?? quoteColumn(key, q, q.carrierId ?? leg?.carrierId ?? null),
    );
  }
  for (const r of outcomes ?? []) {
    if (placed.has(r.carrierId)) continue;
    const column = legColumn(r);
    if (column) columns.push(column);
  }
  // A carrier Signal quotes by hand is never in the markets' check: it has a
  // column of its own, last, until the quote Signal entered speaks for it
  // (BRK-S395).
  for (const c of manualCarriers) {
    if (c.quotingMode !== "manual") continue;
    if (columns.some((col) => col.carrierId === c.carrierId)) continue;
    columns.push({
      key: c.carrierId,
      carrier: c.carrierName,
      carrierId: c.carrierId,
      quote: null,
      outcome: null,
      reasons: [],
      appetite: null,
      missingFields: [],
      manual: { request: c.manualQuoteRequest },
    });
  }
  return columns;
}
